import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Globe, Loader2, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../../components/ui/select";
import api, { formatApiError } from "../../lib/api";

const CATS = ["Direct Tax", "GST & Indirect Tax", "Audit Standards", "Corporate Law", "ESG & Risk"];

function DraftForm({ d, setD }) {
  const set = (k) => (e) => setD({ ...d, [k]: e.target.value });
  return (
    <div className="space-y-3">
      <label className="block"><span className="text-xs text-slate-500">Title</span><Input value={d.title} onChange={set("title")} data-testid="insight-title" /></label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block"><span className="text-xs text-slate-500">Category</span>
          <Select value={d.category} onValueChange={(v) => setD({ ...d, category: v })}>
            <SelectTrigger data-testid="insight-category"><SelectValue /></SelectTrigger>
            <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </label>
        <label className="block"><span className="text-xs text-slate-500">Author</span><Input value={d.author} onChange={set("author")} data-testid="insight-author" /></label>
      </div>
      <label className="block"><span className="text-xs text-slate-500">Summary (shown on the card)</span><Textarea rows={2} value={d.summary} onChange={set("summary")} data-testid="insight-summary" /></label>
      <label className="block"><span className="text-xs text-slate-500">Article</span><Textarea rows={10} value={d.body} onChange={set("body")} data-testid="insight-body" /></label>
      <label className="block"><span className="text-xs text-slate-500">Source link (optional)</span><Input value={d.external_url} onChange={set("external_url")} data-testid="insight-url" /></label>
    </div>
  );
}

export default function PublishInsight({ update, onPublished }) {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const draft = async () => {
    setBusy(true);
    try { setD((await api.post(`/website/insights/draft/${update.id}`)).data); }
    catch (e) { toast.error(formatApiError(e)); setOpen(false); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (open && !d) draft(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const publish = async () => {
    setPublishing(true);
    try {
      const { existing_id, ...body } = d;
      const r = await api.post("/website/insights/publish", body);
      toast.success("Published to Insights");
      onPublished(update.id, r.data.id);
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPublishing(false); }
  };

  if (update.published_insight_id) {
    return (
      <a href={`/lets-learn?read=${update.published_insight_id}`} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 hover:underline inline-flex items-center gap-1" data-testid={`insight-live-${update.id}`}>
        <Globe className="h-3 w-3" /> On website <ExternalLink className="h-3 w-3" />
      </a>
    );
  }
  return (
    <>
      <button onClick={() => setOpen(true)} className="text-xs text-[#9a7b3e] hover:underline inline-flex items-center gap-1" data-testid={`publish-insight-${update.id}`}>
        <Globe className="h-3 w-3" /> Publish to Insights
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="insight-dialog">
          <DialogHeader>
            <DialogTitle>Publish to website Insights</DialogTitle>
            <DialogDescription>AI has drafted a short article from this update. Review and edit before publishing.</DialogDescription>
          </DialogHeader>
          {busy || !d ? (
            <div className="py-16 text-center text-sm text-slate-500" data-testid="insight-drafting"><Loader2 className="h-5 w-5 animate-spin mx-auto mb-3" />Drafting article…</div>
          ) : <DraftForm d={d} setD={setD} />}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={draft} disabled={busy} data-testid="insight-redraft"><RefreshCw className="h-4 w-4 mr-1" /> Redraft</Button>
            <Button onClick={publish} disabled={busy || !d || publishing} data-testid="insight-publish-btn">{publishing ? "Publishing…" : "Publish"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
